import React, { FC, useCallback } from "react";
import { Block } from "kanopi-components/Block";
import { Button, KIND, SIZE as BUTTON_SIZE } from "kanopi-components/Button";
import { LabelSmall } from "kanopi-components/typography";
import { ParagraphSmall } from "baseui/typography";
import { Plus } from "baseui/icon";
import { SIZE as TAG_SIZE, VARIANT as TAG_VARIANT } from "baseui/tag";
import { TableBuilder } from "kanopi-components/TableView/TableView";
import { Tag } from "kanopi-components/Tag";
import { Textarea } from "baseui/textarea";
import { useAPI } from "kw/api/useAPI";
import { useFormatter } from "kw/util/useFormatter";
import { useStyletron } from "kanopi-components/theme/useStyletron";

type TAdminNotesSection = {
  notes: TNote[];
  assocObjModel: string;
  assocObjId: string;
  fetchAssocObj: (assocObjId: string) => void;
};
export const AdminNotesSection: FC<TAdminNotesSection> = ({
  notes,
  assocObjId,
  assocObjModel,
  fetchAssocObj,
}) => {
  const { formatDateTime, formatString } = useFormatter();
  const columnSchema = [
    {
      children: (row: TNote) => (
        <>
          <ParagraphSmall
            marginTop={0}
            marginLeft={theme.sizing.scale100}
            whiteSpace="pre-wrap"
          >
            <div
              style={{ marginTop: 0, marginBottom: 0 }}
              dangerouslySetInnerHTML={{ __html: row.content }}
            />
          </ParagraphSmall>
          <Tag variant={TAG_VARIANT.outlined} size={TAG_SIZE.small}>
            {formatString(`${row?.author?.firstName} ${row?.author?.lastName}`)}
          </Tag>
          {formatDateTime(row?.created)}
        </>
      ),
      sortable: false,
    },
  ];

  const [noteContent, setNoteContent] = React.useState("");
  const [addingNote, setAddingNote] = React.useState(false);
  const { mutation, loading: savingNote } = useAPI();
  const [, theme] = useStyletron();

  const saveNote = useCallback(() => {
    const content = noteContent.trim();
    content &&
      mutation(
        {
          admin: {
            createNote: [
              {
                assocObjModel: assocObjModel,
                assocObjId: assocObjId,
                content: content,
              },
              {
                id: true,
              },
            ],
          },
        },
        () => {
          setAddingNote(false);
          setNoteContent("");
          fetchAssocObj(assocObjId);
        }
      );
  }, [noteContent]);

  const discardNote = useCallback(() => {
    setAddingNote(false);
    setNoteContent("");
  }, []);

  const onChangeNote = useCallback((e) => {
    setNoteContent(e.currentTarget.value);
  }, []);

  return (
    <>
      <Block display="flex" flexDirection="column" gridRowGap={theme.sizing.scale700}>
        <Block display="flex" alignItems={"center"} gridGap={theme.sizing.scale700}>
          {!notes?.length && <LabelSmall>No notes found</LabelSmall>}
          <Button
            size={BUTTON_SIZE.compact}
            kind={KIND.tertiary}
            disabled={addingNote}
            outlined={true}
            startEnhancer={<Plus />}
            onClick={() => {
              setAddingNote(true);
            }}
          >
            Add a note
          </Button>
        </Block>

        {addingNote && (
          <>
            <Textarea
              value={noteContent}
              onChange={onChangeNote}
              overrides={{
                Input: {
                  style: {
                    whiteSpace: "pre-wrap",
                  },
                },
              }}
            />
            <Block
              display="flex"
              justifyContent="flex-end"
              gridColumnGap={theme.sizing.scale700}
            >
              <Button
                size={BUTTON_SIZE.compact}
                disabled={savingNote}
                onClick={discardNote}
              >
                Discard
              </Button>
              <Button
                size={BUTTON_SIZE.compact}
                onClick={saveNote}
                isLoading={savingNote}
              >
                Save
              </Button>
            </Block>
          </>
        )}
      </Block>

      {notes?.length > 0 && (
        <TableBuilder
          columnSchema={columnSchema}
          data={notes}
          showSearchSection={false}
          showPagination={false}
        />
      )}
    </>
  );
};
